'use client';

import { ReactNode } from 'react';
import classnames from 'classnames/bind';
import { useParams } from 'next/navigation';
import { EHelpCenterType } from '@portals/help-center-core';
import { useLocale } from '@/_hooks/use-locale';
import { CategoryButton } from '@/components/category-button';
import { categoryClickEvent } from '@/_analytic/events/category-click-event';
import { subsectionClickEvent } from '@/_analytic/events/subsection-click-event';
import {
  getChildCategoryRoute,
  getParentCategoryRoute,
  getUrl,
  TMultiPageRouteParams,
} from '@/_constants/routes';
import { useRouter } from '@/i18n/navigation';
import { useExpandedState } from '@/_hooks/use-expanded-state';
import {
  TMaterialsTreeCategoryItem,
  TMaterialsTreeItem,
} from '@/api/get-materials-tree/_types';
import { MATERIAL_CATEGORY_ID } from '@/_types/route-params';
import { useClientSearchParams } from '@/_hooks/use-client-search-params';
import { useScrollIntoView } from '../../_hooks/use-scroll-into-view';
import { getOpenIds } from './_utils/get-open-ids';
import styles from './index.module.scss';

const cn = classnames.bind(styles);
const BLOCK_NAME = 'client-category-node-wrapper';

type TProps = {
  category: TMaterialsTreeItem | TMaterialsTreeCategoryItem;
  children: ReactNode;
  isExpandable?: boolean;
  elementType: 'category' | 'section';
  parentDict: Record<string, string>;
  parentCategoryTitle?: string;
};

export const ClientCategoryNodeWrapper = ({
  category,
  children,
  isExpandable: isExpandableProps,
  elementType,
  parentCategoryTitle,
  parentDict,
}: TProps) => {
  const { country } = useParams<TMultiPageRouteParams>();
  const searchParams = useClientSearchParams();
  const params = useParams<TMultiPageRouteParams>();

  const selectedCategoryId =
    params.parentCategoryId ||
    params.childCategoryId ||
    searchParams[MATERIAL_CATEGORY_ID];

  const router = useRouter();
  const locale = useLocale();
  const categoryName = category.title;

  const openIds = getOpenIds({
    selectedId: selectedCategoryId,
    subcategoryId: searchParams[MATERIAL_CATEGORY_ID],
    parentDict: parentDict,
  });

  const isOpen = openIds.has(category.id) || openIds.has(category.slug);

  useScrollIntoView({ id: category.id, isOpen });

  const { isArrowExpanded, isCategoryExpanded, toggleExpandedState } =
    useExpandedState(isOpen);

  const handleClick = () => {
    toggleExpandedState();

    if (elementType === 'category') {
      categoryClickEvent({
        isSidebar: true,
        sectionName: categoryName,
        sectionId: category.id,
        helpCenterType: EHelpCenterType.MAIN,
        isDisabled: false,
      });

      router.push(
        getUrl({
          route: getParentCategoryRoute({
            parentCategoryId: category.id,
            parentCategorySlug: category.slug,
            country,
          }),
        }),
      );

      return;
    }

    subsectionClickEvent({
      sectionName: parentCategoryTitle || '',
      sectionId: 'parentId' in category ? category.parentId : null,
      subsectionName: categoryName,
      subsectionId: category.id,
      isSidebar: true,
      isDisabled: false,
      locale,
    });

    router.push(
      getUrl({
        route: getChildCategoryRoute({
          childCategoryId: category.slug || category.id,
          country,
        }),
      }),
    );
  };

  const isActive = isOpen && elementType === 'section';
  const isExpandable = isExpandableProps && elementType === 'section';
  const onClick = isExpandableProps ? handleClick : undefined;

  return (
    <div
      className={cn(BLOCK_NAME, {
        [`${BLOCK_NAME}--is-open`]: isCategoryExpanded,
      })}
    >
      <CategoryButton
        id={category.id}
        isActive={isActive}
        isDisabled={!isExpandableProps}
        isExpandable={isExpandable}
        isExpanded={isArrowExpanded}
        isTextClamped
        onClick={onClick}
        text={categoryName}
      />
      {/* Рендерим children всегда. CSS управляет видимостью */}
      {children}
    </div>
  );
};
